/// FOURJS_START_COPYRIGHT(D,2025)
/// Property of Four Js*
/// (c) Copyright Four Js 2025, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("OfflineModeService", ["InitService"], function (context, cls) {
  /**
   * Service managing the browser's online/offline state for GBC
   * It enables or disables the application's offline mode
   * @namespace gbc.OfflineModeService
   * @gbcService
   * @publicdoc
   */
  class OfflineModeService /** @lends gbc.OfflineModeService */ {
    constructor() {
      this._onOnline = this._onOnline.bind(this);
      this._onOffline = this._onOffline.bind(this);
      this._enabled = true;

      this._floatingBanner = null;

      this._message = null;
      this._messageService = null;
    }

    /**
     * @inheritDoc
     */
    init() {
      this._applyOfflineModePolicy();

      // Listen to connectivity changes
      globalThis.addEventListener("online", this._onOnline);
      globalThis.addEventListener("offline", this._onOffline);
    }

    /**
     * @inheritDoc
     */
    destroy() {
      globalThis.removeEventListener("online", this._onOnline);
      globalThis.removeEventListener("offline", this._onOffline);

      if (this._floatingBanner) {
        this._floatingBanner.destroy();
        this._floatingBanner = null;
      }

      this._message = null;
      this._messageService = null;
    }

    /**
     * Enables the offline mode handling
     * Calls to online/offline handlers will be processed again
     */
    enable() {
      this._enabled = true;
    }

    /**
     * Disables the offline mode handling
     * The modal and reconnect message are hidden and online/offline events are ignored
     */
    disable() {
      this._enabled = false;

      if (this._modal) {
        this._modal.hide();
      }
      if (this._message) {
        this._message.setHidden(true);
      }
    }

    /**
     * Returns true if offline mode handling is currently enabled
     * @returns {boolean}
     */
    isServiceEnabled() {
      return this._enabled;
    }

    /**
     * Applies offline mode settings
     * Priority: query string, then theme variable, then default behavior
     */
    _applyOfflineModePolicy() {
      // Priority 1: query string overrides
      if (cls.WindowHelper.isURLParameterEnabled(globalThis.location.search, "offlinemode")) {
        this.enable();
        return;
      }
      if (cls.WindowHelper.isURLParameterDisabled(globalThis.location.search, "offlinemode")) {
        this.disable();
        return;
      }

      // Priority 2: theme variable
      const themeMode = String(context.ThemeService.getValue("gbc-offline-mode") || "auto").toLowerCase();
      if (themeMode === "enabled") {
        this.enable();
        return;
      }
      if (themeMode === "disabled") {
        this.disable();
        return;
      }

      // Priority 3: base behavior (wrapper + localhost)
      if (!globalThis.gbcWrapper.isOfflineModeEnabled() || this._isLocalhost()) {
        this.disable();
      }
    }

    /**
     * Handles the browser going online
     * Hides the offline modal and shows a reconnect message
     */
    _onOnline() {
      if (!this._floatingBanner || !this._enabled) {
        return;
      }
      this._floatingBanner.hide();

      this._initMessageWidget();

      if (this._message && this._messageService) {
        this._message.setText(gbc.I18NService.t("gwc.offlineMode.reconnectedMessage"));
        this._message.setMessageColor("white", context.ThemeService.getValue("gbc-MessageWidget-success-background-color"));
        this._message.setHidden(false);
        this._messageService.addMessage("offline", this._message);
        this._messageService.handlePositions();
      }

      context.SessionService.getCurrent()?.getCurrentApplication()?.protocolInterface.uaProtocol?.ping();
    }

    /**
     * Handles the browser going offline
     * Hides any reconnect message and shows the offline modal
     */
    _onOffline() {
      if (!this._enabled) {
        return;
      }

      this._ensureFloatingBanner();

      this._floatingBanner.setBackdrop(true);
      this._floatingBanner.setIcon("zmdi-wifi-off");
      this._floatingBanner.setIconColor(context.ThemeService.getValue("mt-orange"));
      this._floatingBanner.setZIndex(context.ThemeService.getValue("z-index-above-DropDown"));
      this._floatingBanner.setHeaderContent(this._buildOfflineBannerContent());
      this._floatingBanner.setSwipeEnabled(false);
      this._floatingBanner.show();

      if (this._message) {
        this._message.setHidden(true);
      }
    }

    _isLocalhost() {
      const host = globalThis.location?.hostname;
      return host === "localhost" || host === "127.0.0.1" || host === "::1";
    }

    /**
     * Returns true if offline mode is currently active:
     * browser is offline, feature is enabled and not ignored
     * @returns {boolean}
     */
    isOfflineModeActive() {
      return !navigator.onLine && this._enabled;
    }

    /**
     * Ensure that the offline modal is created and attached to the DOM
     */
    _ensureFloatingBanner() {
      if (this._floatingBanner) {
        return;
      }

      this._floatingBanner = cls.WidgetFactory.createWidget("FloatingBanner", { appHash: gbc.systemAppId });
      document.body.appendChild(this._floatingBanner.getElement());
    }

    _buildOfflineBannerContent() {
      const wrapper = document.createElement("span");

      const strong = document.createElement("strong");
      strong.textContent = gbc.I18NService.t("gwc.offlineMode.title");

      wrapper.appendChild(strong);
      wrapper.appendChild(document.createTextNode(" " + gbc.I18NService.t("gwc.offlineMode.message")));

      return wrapper;
    }

    /**
     * Initializes the message widget used to display reconnect messages
     * Does nothing if the widget is already initialized or no application is available
     */
    _initMessageWidget() {
      if (this._message) {
        return;
      }

      const application = context.SessionService.getCurrent()?.getCurrentApplication();
      if (!application) {
        return;
      }

      const userInterfaceNode = application.getNode(0);
      const userInterfaceWidget = userInterfaceNode.getController().getWidget();
      const msgWidget = userInterfaceWidget.getMessageWidget();

      this._messageService = application.message;
      this._message = msgWidget;
    }
  }

  gbc.OfflineModeService = new OfflineModeService();
  context.InitService.register(context.OfflineModeService);
});
